import type { Ssm5ConnectErrorCode, Ssm5ConnectPhase, Ssm5Session } from '@packages/domain/ssm5';
import type { PropsWithChildren } from 'react';

import { wait } from '@packages/core/async';
import { getLogger } from '@packages/core/logging';
import { ConnectToVehicle } from '@packages/domain/ssm5';
import { container } from '@web/services/container';
import { useVinSessionState } from '@web/vin-sessions/context/vin-sessions.hooks';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import type {
    Ssm5Connection,
    Ssm5ConnectionState,
    Ssm5LiveConnection,
    Ssm5VehicleMatch,
} from './ssm5-connection.context';

import { checkConnection } from '../helpers/check-connection';
import { findVehicle } from '../helpers/find-vehicle';
import { isSsm5Path } from '../helpers/ssm5-paths';
import { useConnectionStatus } from '../hooks/use-connection-status';
import { EXPECTED_CONNECT_DURATION_MS } from '../ssm5.constants';
import { Ssm5ConnectionContext } from './ssm5-connection.context';

const IDLE: Ssm5ConnectionState = { status: 'idle' };

/** How long the page waits between two checks of the session it is viewing. */
const STATUS_POLL_INTERVAL_MS = 5000;

const [$connectToVehicle] = container.resolveOperations(ConnectToVehicle);
const logger = getLogger('ssm5');

/** The attempt the technician is told is taking long, with the Cloud Connector ID a restart goes through again. */
type SlowAttempt = {
    /** The attempt's controller, the one in flight when it was flagged. */
    attempt: AbortController;
    /** The Cloud Connector ID the attempt was made with. */
    cloudConnectorId: string;
};

/**
 * Own the SSM5 connection for the whole app: one attempt at a time, settled into the open VIN session
 * when the vehicle it read is that session's vehicle, and handed back for the technician to accept
 * otherwise. An attempt that runs longer than expected is flagged for the technician, who either keeps
 * waiting, and is asked again if it runs that long once more, or restarts it through the same Cloud
 * Connector ID. The session stays live while the technician works in other sections, and is dropped
 * only when a VIN session for another vehicle is opened, or when a session the page is about to
 * offer back turns out to have expired while the technician was away. A session the backend reports
 * still open can be shown again the same way, without connecting again. While the page shows the
 * session, it is watched with one status check at a time, and a lost one stays on the page as
 * disconnected until the technician restarts or leaves. The page state follows the technician: it
 * returns to idle whenever they leave the SSM5 page, so the session is offered back through the
 * recent connections rather than reopened behind them.
 *
 * @param props - the subtree that can read the connection
 * @returns the provider
 */
export function Ssm5ConnectionProvider({ children }: PropsWithChildren) {
    const [state, setState] = useState<Ssm5ConnectionState>(IDLE);
    const [liveConnection, setLiveConnection] = useState<Ssm5LiveConnection>();
    const { selectedVin } = useVinSessionState();
    const { pathname } = useLocation();
    const isOnSsm5Page = isSsm5Path(pathname);
    const sessionVin = selectedVin?.vinRecord.vin;
    const isViewingSession = state.status === 'connected' || state.status === 'disconnected';
    const watchedId = state.status === 'connected' ? state.session.cloudConnectorId : undefined;
    const timedId = state.status === 'connecting' && !state.isTakingLong ? state.cloudConnectorId : undefined;
    const attemptRef = useRef<AbortController | null>(null);
    const slowAttemptRef = useRef<SlowAttempt | null>(null);
    const sessionVinRef = useRef(sessionVin);
    const offeredBackId = isOnSsm5Page && !isViewingSession ? liveConnection?.cloudConnectorId : undefined;
    const offeredBackStatus = useConnectionStatus(offeredBackId);

    const dropAttempt = useCallback(() => {
        attemptRef.current?.abort();
        attemptRef.current = null;
    }, []);

    const cancel = useCallback(() => {
        dropAttempt();
        setState((current) => (current.status === 'connected' ? current : IDLE));
    }, [dropAttempt]);

    const dropSession = useCallback(() => {
        setLiveConnection(undefined);
        setState(IDLE);
    }, []);

    const loseConnection = useCallback(() => {
        setLiveConnection(undefined);
        setState((current) => (current.status === 'connected' ? { ...current, status: 'disconnected' } : current));
    }, []);

    // Only an attempt still in flight is flagged; a timer that fires just as its attempt settles changes nothing.
    const flagTakingLong = useCallback((cloudConnectorId: string) => {
        const attempt = attemptRef.current;

        if (attempt === null) {
            return;
        }

        slowAttemptRef.current = { attempt, cloudConnectorId };
        logger.warn(`The SSM5 connect attempt through [${cloudConnectorId}] is taking longer than expected`, {
            cloudConnectorId,
        });
        setState((current) => (current.status === 'connecting' ? { ...current, isTakingLong: true } : current));
    }, []);

    const connect = useCallback(async (cloudConnectorId: string) => {
        if (attemptRef.current) {
            return;
        }

        const attempt = new AbortController();
        attemptRef.current = attempt;
        setState({ cloudConnectorId, isTakingLong: false, phase: 'pairing', progress: 0, status: 'connecting' });

        const settled = await runConnectFlow(
            cloudConnectorId,
            attempt.signal,
            () => sessionVinRef.current,
            (phase, progress) => {
                if (!attempt.signal.aborted) {
                    setState((current) =>
                        current.status === 'connecting' ? { ...current, phase, progress } : current
                    );
                }
            }
        ).catch((error: unknown) => failAttemptUnexpectedly(cloudConnectorId, error, attempt.signal));

        if (attempt.signal.aborted) {
            return;
        }

        attemptRef.current = null;
        setState(settled);
    }, []);

    const keepWaiting = useCallback(() => {
        slowAttemptRef.current = null;
        setState((current) =>
            current.status === 'connecting' && current.isTakingLong ? { ...current, isTakingLong: false } : current
        );
    }, []);

    // A restart acts only on the flagged attempt while it is still the one in flight, read from the refs
    // rather than the last render, so a press that lands after the attempt settled or a second press starts
    // nothing. The attempt is dropped without passing through idle, which only React's batching would hide.
    const restartAttempt = useCallback(() => {
        const slowAttempt = slowAttemptRef.current;

        if (slowAttempt === null || slowAttempt.attempt !== attemptRef.current) {
            return;
        }

        slowAttemptRef.current = null;
        dropAttempt();
        void connect(slowAttempt.cloudConnectorId);
    }, [connect, dropAttempt]);

    const continueToVinSession = useCallback(() => {
        setState((current) => (current.status === 'vehicleMatched' ? { ...current, status: 'connected' } : current));
    }, []);

    const resume = useCallback(
        (connection: Ssm5LiveConnection) => {
            const { cloudConnectorId, vehicle, vin } = connection;
            const match: Ssm5VehicleMatch = { session: { cloudConnectorId, vin }, vehicle };

            setLiveConnection(connection);
            setState(
                sessionVin === undefined ? { ...match, status: 'vehicleMatched' } : { ...match, status: 'connected' }
            );
        },
        [sessionVin]
    );

    // Whatever the page shows as connected is the live session. A session that is already live is
    // kept as is, so resuming it does not re-stamp the time it was first established at.
    useEffect(() => {
        if (state.status !== 'connected') {
            return;
        }

        const { session, vehicle } = state;

        setLiveConnection((live) =>
            isSessionLive(live, session) ? live : { ...session, connectedAt: new Date(), vehicle }
        );
    }, [state]);

    // An attempt settles against the VIN session open when its vehicle is matched, not the one it started in.
    // The ref follows the VIN in the commit itself, so a match that lands right after the VIN session
    // changed cannot read the VIN of the render before.
    useLayoutEffect(() => {
        sessionVinRef.current = sessionVin;
    }, [sessionVin]);

    useEffect(() => cancel, [cancel]);

    useEffect(() => {
        if (isOnSsm5Page) {
            return;
        }

        cancel();
        setState(IDLE);
    }, [cancel, isOnSsm5Page]);

    useEffect(() => {
        if (liveConnection === undefined || sessionVin === undefined || sessionVin === liveConnection.vin) {
            return;
        }

        dropSession();
    }, [dropSession, liveConnection, sessionVin]);

    // Only the session waiting to be offered back is dropped, and only when the backend says it is
    // gone. Whatever the page moved on to while the check was in flight, such as a failed retry, stays
    // on screen; a check that could not reach the backend changes nothing.
    useEffect(() => {
        if (offeredBackStatus === 'gone') {
            setLiveConnection(undefined);
        }
    }, [offeredBackStatus]);

    // The attempt is timed from its start across all its phases. Keeping waiting and restarting both
    // time it again for a full duration, so an attempt that stays stuck is never left unexplained.
    useEffect(() => {
        if (timedId === undefined) {
            return;
        }

        const timer = new AbortController();

        wait(EXPECTED_CONNECT_DURATION_MS, timer.signal)
            .then(() => {
                if (!timer.signal.aborted) {
                    flagTakingLong(timedId);
                }
            })
            .catch(() => undefined);

        return () => timer.abort();
    }, [flagTakingLong, timedId]);

    useEffect(() => {
        if (watchedId === undefined || !isOnSsm5Page) {
            return;
        }

        const watch = new AbortController();

        watchConnection(watchedId, watch.signal)
            .then(() => {
                if (!watch.signal.aborted) {
                    loseConnection();
                }
            })
            .catch(() => undefined);

        return () => watch.abort();
    }, [isOnSsm5Page, loseConnection, watchedId]);

    const connection = useMemo<Ssm5Connection>(
        () => ({
            cancel,
            connect,
            continueToVinSession,
            keepWaiting,
            liveConnection,
            restart: dropSession,
            restartAttempt,
            resume,
            state,
        }),
        [cancel, connect, continueToVinSession, dropSession, keepWaiting, liveConnection, restartAttempt, resume, state]
    );

    return <Ssm5ConnectionContext.Provider value={connection}>{children}</Ssm5ConnectionContext.Provider>;
}

/**
 * Settle an attempt as failed, logged unless the attempt was cancelled meanwhile.
 *
 * @param cloudConnectorId - the Cloud Connector ID the attempt was made with
 * @param errorCode - why it failed
 * @param signal - the attempt's signal, aborted when it was cancelled
 * @returns the failed state
 */
function failAttempt(
    cloudConnectorId: string,
    errorCode: Ssm5ConnectErrorCode,
    signal: AbortSignal
): Ssm5ConnectionState {
    if (!signal.aborted) {
        logger.warn(`The SSM5 connect attempt through [${cloudConnectorId}] failed as [${errorCode}]`, {
            cloudConnectorId,
            errorCode,
        });
    }

    return { cloudConnectorId, errorCode, status: 'failed' };
}

/**
 * Settle an attempt whose flow threw as failed for an unknown reason, logged unless the attempt was
 * cancelled meanwhile.
 *
 * @param cloudConnectorId - the Cloud Connector ID the attempt was made with
 * @param error - what the flow threw
 * @param signal - the attempt's signal, aborted when it was cancelled
 * @returns the failed state
 */
function failAttemptUnexpectedly(cloudConnectorId: string, error: unknown, signal: AbortSignal): Ssm5ConnectionState {
    if (!signal.aborted) {
        logger.error(`The SSM5 connect attempt through [${cloudConnectorId}] failed before it settled`, {
            cloudConnectorId,
            error,
        });
    }

    return { cloudConnectorId, errorCode: 'unknown', status: 'failed' };
}

/**
 * Whether the live session is the given SSM5 session.
 *
 * @param live - the session that is live, or undefined without one
 * @param session - the session to compare it against
 * @returns true when both run on the same Cloud Connector and vehicle
 */
function isSessionLive(live: Ssm5LiveConnection | undefined, session: Ssm5Session): boolean {
    return live !== undefined && live.cloudConnectorId === session.cloudConnectorId && live.vin === session.vin;
}

/**
 * Open the session through the Cloud Connector, then match the VIN it read to a TechShare vehicle.
 *
 * @param cloudConnectorId - the normalized Cloud Connector ID
 * @param signal - aborts the run
 * @param readSessionVin - reads the VIN of the VIN session open once the vehicle is matched, which the match settles into when it is that vehicle
 * @param onPhase - receives each phase and its progress as the run reports them; matching the vehicle starts the progress over
 * @returns the settled state; rejects when aborted or when a step throws
 */
async function runConnectFlow(
    cloudConnectorId: string,
    signal: AbortSignal,
    readSessionVin: () => string | undefined,
    onPhase: (phase: Ssm5ConnectPhase, progress: number) => void
): Promise<Ssm5ConnectionState> {
    const result = await $connectToVehicle({ cloudConnectorId, onPhase, signal });

    if (!result.success) {
        return failAttempt(cloudConnectorId, result.errorCode, signal);
    }

    onPhase('matchingVehicle', 0);
    const vehicle = await findVehicle(result.session.vin, signal);

    if (!vehicle) {
        return failAttempt(cloudConnectorId, 'vehicleNotFound', signal);
    }

    const match: Ssm5VehicleMatch = { session: result.session, vehicle };
    const isSessionVehicle = result.session.vin === readSessionVin();

    return isSessionVehicle ? { ...match, status: 'connected' } : { ...match, status: 'vehicleMatched' };
}

/**
 * Watch a session until it is gone: one status check per interval, the next scheduled only once the
 * previous one settled, so no two checks ever overlap. A check that throws is not a loss and the
 * watch goes on.
 *
 * @param cloudConnectorId - the Cloud Connector ID the session runs through
 * @param signal - aborts the watch
 * @returns resolves once the session is reported gone; rejects when aborted
 */
async function watchConnection(cloudConnectorId: string, signal: AbortSignal): Promise<void> {
    await wait(STATUS_POLL_INTERVAL_MS, signal);

    if ((await checkConnection(cloudConnectorId, signal)) !== 'gone') {
        return watchConnection(cloudConnectorId, signal);
    }
}
